import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './EmiSection.css';
import {
  cardVariantsLeft,
  cardVariantsRight,
  cardVariants,
  hoverVariants,
  textVariants,
  listItemVariants,
  factorItemHover,
  exampleBoxHover
} from '../../utility/framerAnimations';

const EmiInfoSection = () => {
  return (
    <Container className="emi-info-section">
      <Row className="justify-content-center w-100">
        <Col lg={12} md={12}>
          <div className="info-content">
            
            {/* Section 1: What Is an EMI Calculator? - From Left */}
            <motion.div 
              className="info-card "
              variants={cardVariantsLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                What Is an EMI Calculator?
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                An EMI calculator helps you compute EMI for various loans such as home loans, car loans, personal loans, and education loans online for free. It computes EMI in seconds and shows a breakdown of principal and interest components. This tool aids in comparing loan options and planning your monthly budget effectively.
              </motion.p>
            </motion.div>

            {/* Section 2: How Does an EMI Calculator Work? - From Right */}
            <motion.div 
              className="info-card"
              variants={cardVariantsRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                How Does an EMI Calculator Work?
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                Using an online EMI calculator is simple and straightforward. It considers three main factors: loan amount, interest rate, and loan duration. Once you enter these details, it displays the EMI along with a detailed breakdown of the principal amount, interest amount, and total payable.
              </motion.p>
            </motion.div>

            {/* Section 3: How To Use Our Loan EMI Calculator? - From Left */}
            <motion.div 
              className="info-card "
              variants={cardVariantsLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                How To Use Our Loan EMI Calculator?
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                Using our EMI calculator is easy and effortless. Simply open it online and follow these simple steps:
              </motion.p>
              <motion.ul 
                className="steps-list"
              >
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  Enter the loan amount
                </motion.li>
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  Enter the interest rate
                </motion.li>
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                >
                  Enter the duration of the loan
                </motion.li>
              </motion.ul>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                The calculator provides quick results with a breakdown of total payable, interest, and principal.
              </motion.p>
              
              {/* Example Section */}
              <motion.div 
                className="example-box mt-4"
                variants={cardVariants}
                whileHover="hover"
                hoverVariants={exampleBoxHover}
              >
                <motion.h4 
                  className="example-heading"
                  variants={textVariants}
                >
                  Example:
                </motion.h4>
                <motion.p 
                  className="section-text"
                  variants={textVariants}
                >
                  Let's say you want a loan of ₹1,20,000 for wedding expenses with a 7% interest rate and 3-year duration.
                </motion.p>
                <motion.ul 
                  className="example-list"
                >
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <strong>EMI:</strong> ₹3,705/month
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <strong>Principal amount:</strong> ₹1,20,000
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <strong>Interest:</strong> ₹13,389
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 1.1 }}
                  >
                    <strong>Total Payable:</strong> ₹1,33,389
                  </motion.li>
                </motion.ul>
              </motion.div>
            </motion.div>

            {/* Section 4: Factors Affecting Your EMI Amount - From Right */}
            <motion.div 
              className="info-card"
              variants={cardVariantsRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                Factors Affecting Your EMI Amount
              </motion.h2>
              <motion.div 
                className="factors-list"
              >
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    Interest rate:
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    A higher interest rate increases your EMI as you pay more interest on the loan amount.
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    Loan tenure:
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    Shorter tenures lead to higher EMIs, while longer tenures result in lower EMIs but higher total interest.
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    Type of interest:
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    Fixed or floating interest rates affect your EMI. Floating rates may change during the loan tenure.
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    Credit score:
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    A better credit score can lead to lower interest rates, thereby affecting your EMI amount.
                  </motion.p>
                </motion.div>
              </motion.div>
            </motion.div>

          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmiInfoSection; 